import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';

import { IMPERSONATION_TOKEN } from '@auth/custom-providers';
import { AuthService } from '@auth/auth.service';

import { PermissionNames } from '@shared/interfaces/permissions.interface';
import { FullNamePipe } from '@shared/pipes/fullname.pipe';
import { IUser } from '@shared/user.interface';

import { ProfileInfo } from '@data-access/profiles/types';
import {
  UserChatData,
  generateChatKey,
} from '@data-access/talk-desk/types/talkdesk.type';
import { TALKDESK_ACTIONS } from '@data-access/talk-desk';
import { UserPermission } from '@data-access/user-management';

import { User } from '@iris-sites/interfaces/sites.interfaces';

@Component({
  imports: [FullNamePipe, MatMenuModule, MatIconModule],
  selector: 'wcg-profile-header-wrapper',
  styleUrls: ['./profile-header.wrapper.component.css'],
  templateUrl: './profile-header.wrapper.component.html',
})
export class ProfileHeaderWrapperComponent {
  private readonly _store = inject(Store);
  private readonly _authService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _session = inject(IMPERSONATION_TOKEN);

  readonly data = input<ProfileInfo>({} as ProfileInfo);
  readonly viewedUserId = input.required<string>();
  readonly isInternal = input<boolean>(false);
  readonly currentUser = input<IUser | undefined>(undefined);
  readonly skipImpersonation = input<boolean>(false);

  private readonly _currentViewUser = toSignal(
    this._authService.user$.pipe(
      filter((userDetails) => !!userDetails),
      takeUntilDestroyed(this._destroyRef),
    ),
  ) as Signal<User | undefined>;

  private readonly _$permissions = computed(() => {
    const _currentViewedUser = this._currentViewUser();
    if (!_currentViewedUser) return [];
    return _currentViewedUser.role?.permissions ?? [];
  });

  private readonly _canImpersonateExternalUser = computed(() =>
    this._$permissions().some(
      (permission: UserPermission) =>
        permission.name === PermissionNames.ImpersonateExternalUser &&
        permission.can_edit,
    ),
  );

  private readonly _canImpersonateInternalUser = computed(() =>
    this._$permissions().some(
      (permission: UserPermission) =>
        permission.name === PermissionNames.ImpersonateInternalUser &&
        permission.can_edit,
    ),
  );

  readonly $canImpersonate = computed(() => {
    const _skipImpersonation = this.skipImpersonation();

    // If user is not active, impersonation should not be allowed, regardless of permissions
    const _isUserActive = this.data()?.status?.toLowerCase() === 'active';
    if (!_isUserActive) return false;

    return (
      (this.isInternal()
        ? this._canImpersonateInternalUser()
        : this._canImpersonateExternalUser()) && !_skipImpersonation
    );
  });

  contactClientCare() {
    const user = this.currentUser();
    if (!user) return;
    const chatData: UserChatData = {
      contextType: 'user',
      isOpen: true,
      primaryId: user.id,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      user: user,
      question: 'Request to change name or userID',
    };
    const chatId = generateChatKey('user', user.id);
    this._store.dispatch(TALKDESK_ACTIONS.openChat({ chatId, chatData }));
  }

  impersonateProfile(): void {
    this._authService
      .impersonateUser(this.viewedUserId())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._session.set();
        window.location.href = '/home';
      });
  }
}
